<!--
// app/components/pages/region-world-cup/NewMatchItemBracketWorldCup.vue
-->
<script setup lang="ts">
import type { Match } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import { slugify, setAliasUrl } from "~/src/Shared/utils";
import nuxtLink from "#app/components/nuxt-link";

type Props = {
  match: Match | undefined;
  teamsObj: Record<number, Team>;
  nameNoBold?: boolean;
  externalLink?: boolean;
  isLastChanceMatch?: boolean;
  noBorder?: boolean;
};

const props = defineProps<Props>();

const localePath = useLocalePath();

const placeholderTeamImg = "/img/world-cup/placeholder-team-kwc.png";
</script>

<template>
  <div
    :class="[
      'flex items-center justify-between',
      match?.metaInformation?.homeTeam && match?.metaInformation?.awayTeam ? 'gap-3' : 'gap-0',
    ]"
  >
    <div class="match-participant-info kwc-color">
      <p v-if="match?.metaInformation?.homeTeam" class="text-xl font-bold leading-none">
        {{ match?.metaInformation?.homeTeam || "" }}
      </p>
      <p v-if="match?.metaInformation?.previousRound || isLastChanceMatch" class="text-xs leading-none">
        {{
          $t(
            `bracketKwc.matchOrigin.description.${isLastChanceMatch ? "loser" : match?.metaInformation?.previousRound}`
          )
        }}
      </p>
    </div>

    <component
      :is="!!match ? nuxtLink : 'div'"
      :to="
        !!match && teamsObj
          ? localePath({
              name: 'region-matches-matchId-homeTeam-vs-awayTeam',
              params: {
                region: 'world-cup',
                matchId: match?.id,
                homeTeam: slugify(teamsObj[match?.participants.homeTeamId].shortName || ''),
                awayTeam: slugify(teamsObj[match?.participants.awayTeamId].shortName || ''),
              },
            })
          : undefined
      "
      :external="externalLink"
      :class="[
        'match-container kwc-border-color',
        { '!min-w-44': match?.status?.includes('ended') },
        { '!border-none': noBorder },
      ]"
    >
      <NuxtImg
        :src="
          match?.participants.homeTeamId === 58 || !match
            ? placeholderTeamImg
            : setAliasUrl(teamsObj[match?.participants.homeTeamId].logo.url)
        "
        :alt="!match ? 'placeholder' : teamsObj[match?.participants.homeTeamId].shortName"
        width="40"
        height="40"
        class="w-10 h-10 object-contain object-center"
      />

      <p
        v-if="
          (match?.scores.awayScore || match?.scores.awayScore === 0) &&
          (match?.scores.homeScore || match?.scores.homeScore === 0) &&
          match?.status?.includes('ended')
        "
        class="flex items-baseline justify-center min-w-max shrink-0"
      >
        <span class="text-primary text-xs me-0.5">{{
          match.scores.homeScoreP || match.scores.homeScoreP === 0 ? match.scores.homeScoreP : ""
        }}</span>
        <span class="font-bold text-lg">{{ match.scores.homeScore }}</span>
        <img
          src="/img/nations/kwcn-crown.png"
          alt="kwcn crown"
          width="14"
          height="13"
          class="w-3 pb-[1px] mx-0.5"
        />
        <span class="font-bold text-lg">{{ match.scores.awayScore }}</span>
        <span class="text-primary text-xs ms-0.5">{{
          match.scores.awayScoreP || match.scores.awayScoreP === 0 ? match.scores.awayScoreP : ""
        }}</span>
      </p>
      <p v-else :class="['kwc-color text-lg', nameNoBold ? 'font-normal text-sm' : 'font-semibold']">
        {{ match?.metaInformation?.matchName || "VS" }}
      </p>

      <NuxtImg
        :src="
          match?.participants.awayTeamId === 58 || !match
            ? placeholderTeamImg
            : setAliasUrl(teamsObj[match?.participants.awayTeamId].logo.url)
        "
        :alt="!match ? 'placeholder' : teamsObj[match?.participants.awayTeamId].shortName"
        width="40"
        height="40"
        class="w-10 h-10 object-contain object-center"
      />
    </component>

    <div class="match-participant-info kwc-color">
      <p v-if="match?.metaInformation?.awayTeam" class="text-xl font-bold leading-none">
        {{ match?.metaInformation?.awayTeam }}
      </p>
      <p v-if="match?.metaInformation?.previousRound || isLastChanceMatch" class="text-xs leading-none">
        {{
          $t(
            `bracketKwc.matchOrigin.description.${isLastChanceMatch ? "winner" : match?.metaInformation?.previousRound}`
          )
        }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.match-participant-info {
  @apply text-center uppercase;
}

.match-container {
  @apply border inline-flex items-center justify-between p-1 gap-1 min-w-[121px] !shrink-0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(0, 0, 0, 1) 100%
  );
}

.match-container:hover {
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(255, 255, 255, 0.12) 15%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.12) 85%,
    rgba(0, 0, 0, 1) 100%
  );
}
</style>
